/**
 * Remove hard-coded text colors and backgrounds from Froala/HTML fragments
 * at display time and on paste.
 *
 * Pastes from Word / Google Docs often include inline `color` and
 * `background` / `background-color` (including shorthand). Those override
 * theme colors and become unreadable after a light/dark switch. Class-based
 * Froala styles (`is-white`, `is-highlighted`, …) and other inline styles
 * (font-size, alignment, padding) are preserved.
 *
 * Persistence uses {@link stripThemeBreakingBackgrounds} instead, so inline
 * text color stays in stored HTML.
 *
 * @param {string|null|undefined} html
 * @returns {string}
 */
export function stripInlineTextColors(html) {
    return stripThemeStyles(html, { textColor: true });
}

/**
 * Remove inline backgrounds before Froala HTML is saved or backfilled.
 *
 * Strips `background`, `background-color` (keeping `url()` images), and the
 * legacy `bgcolor` attribute. Inline `color` and the legacy `color` attribute
 * are left in place.
 *
 * @param {string|null|undefined} html
 * @returns {string}
 */
export function stripThemeBreakingBackgrounds(html) {
    return stripThemeStyles(html, { textColor: false });
}

function stripThemeStyles(html, { textColor }) {
    if (html == null) {
        return '';
    }

    if (typeof html !== 'string') {
        return html;
    }

    if (html === '' || !hasThemeBreakingInlineStyle(html, textColor)) {
        return html;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div id="__strip-inline-text-colors">${html}</div>`, 'text/html');
    const root = doc.getElementById('__strip-inline-text-colors');

    // Fail safe: a stray `</div>` closes the wrapper early and the rest of the
    // fragment would be dropped from `root.innerHTML`. Leave such HTML untouched.
    if (!root || !wrapperHoldsAllContent(doc, root)) {
        return html;
    }

    root.querySelectorAll('[style]').forEach(element => {
        const style = element.getAttribute('style');
        if (!style) {
            return;
        }

        const cleaned = style
            .split(';')
            .map(part => part.trim())
            .filter(Boolean)
            .filter(declaration => !isThemeBreakingDeclaration(declaration, textColor))
            .join('; ');

        if (cleaned) {
            element.setAttribute('style', cleaned);
        } else {
            element.removeAttribute('style');
        }
    });

    if (textColor) {
        root.querySelectorAll('[color]').forEach(element => {
            element.removeAttribute('color');
        });
    }

    root.querySelectorAll('[bgcolor]').forEach(element => {
        element.removeAttribute('bgcolor');
    });

    return root.innerHTML;
}

/**
 * True when every element, comment and non-whitespace text node of the parsed
 * document sits inside the wrapper (ignoring the html/head/body scaffolding).
 */
function wrapperHoldsAllContent(doc, root) {
    const walker = doc.createTreeWalker(doc, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT | NodeFilter.SHOW_COMMENT);

    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (root.contains(node)) {
            continue;
        }

        if (node.nodeType === Node.ELEMENT_NODE && ['HTML', 'HEAD', 'BODY'].includes(node.nodeName)) {
            continue;
        }

        if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim() === '') {
            continue;
        }

        return false;
    }

    return true;
}

function hasThemeBreakingInlineStyle(html, textColor) {
    if (textColor) {
        return /(?:background(?:-color)?|color)\s*[:=]|bgcolor\s*=/i.test(html);
    }

    return /(?:background(?:-color)?\s*[:=]|bgcolor\s*=)/i.test(html);
}

function isThemeBreakingDeclaration(declaration, textColor) {
    if (textColor && /^color\s*:/i.test(declaration)) {
        return true;
    }

    if (/^background-color\s*:/i.test(declaration)) {
        return true;
    }

    if (/^background\s*:/i.test(declaration)) {
        return !/url\s*\(/i.test(declaration);
    }

    return false;
}
